'use client'
import type { PartialMessage } from '@bufbuild/protobuf'
import { createConnectQueryKey, useMutation } from '@connectrpc/connect-query'
import { StoreProductType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/store_product_type_pb'
import {
  addStoreCartItemPublic,
  emptyStoreCartItemsPublic,
  getStoreCartPublic,
} from '@fingertip/creator-proto/gen/fingertip/creator/store_cart/v1/store_cart-StoreCartService_connectquery'
import type { GetStoreProductPublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/store_product/v1/store_product_pb'
import { MinusLargeIcon, PlusLargeIcon } from '@fingertip/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'nextjs-toploader/app'
import { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import NotFound from '@/app/not-found'
import { DEFAULT_WIDTH } from '@/components/blocks/shared/constants'
import { ProductMediaCarousel } from '@/components/product/product-media-carousel'
import { getProductPrice } from '@/components/product/product-price'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useToken } from '@/lib/hooks/use-token'
import { RootStoreContext } from '@/lib/stores/root-store'

import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { ProductVariantSelector } from './product-variant-selector'

type Props = {
  siteSlug: string
  productSlug: string
  productData: PartialMessage<GetStoreProductPublicResponse>
  isModal?: boolean
  domain?: string
}

export const PublicProductInner = ({
  siteSlug,
  productSlug,
  productData,
  isModal,
  domain,
}: Props) => {
  const { t } = useTranslation()

  const { callOptions } = useToken()
  const [quantity, setQuantity] = useState(1)
  const [startIndex, setStartIndex] = useState(0)
  const {
    publicStore: { setShouldRefetchCart },
  } = useContext(RootStoreContext)

  const manyVariants = (productData?.productVariants || []).length > 1

  const [selectedVariant, setSelectedVariant] = useState(
    productData?.productVariants?.[0] || null,
  )

  const breakpoint = useBreakpoint()
  const router = useRouter()
  const queryClient = useQueryClient()

  const mutationAdd = useMutation(addStoreCartItemPublic, {
    callOptions,
    onError: (error: any) => {
      toast.error(error.message)
    },
  })

  const mutationEmptyCart = useMutation(emptyStoreCartItemsPublic, {
    callOptions,
    onError: (error: any) => {
      toast.error(error.message)
    },
  })

  const handleAddToCart = useCallback(async () => {
    await mutationAdd.mutateAsync({
      siteSlug,
      productSlug,
      productVariantId: selectedVariant?.id,
      quantity,
    })

    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey(getStoreCartPublic, { siteSlug }),
    })

    setShouldRefetchCart(true)

    toast.success(t('added_to_cart_succes'), {
      position: 'top-center',
    })

    if (isModal) {
      router.back()
    }
  }, [
    isModal,
    mutationAdd,
    productSlug,
    quantity,
    queryClient,
    router,
    selectedVariant?.id,
    setShouldRefetchCart,
    siteSlug,
    t,
  ])

  const handleBuyItNow = useCallback(async () => {
    await mutationEmptyCart.mutateAsync({ siteSlug })

    await mutationAdd.mutateAsync({
      siteSlug,
      productSlug,
      productVariantId: selectedVariant?.id,
      quantity,
    })

    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey(getStoreCartPublic, { siteSlug }),
    })

    setShouldRefetchCart(true)

    router.push(`${domain ? '/' : `/${siteSlug}/`}checkout`)
  }, [
    mutationEmptyCart,
    mutationAdd,
    productSlug,
    quantity,
    queryClient,
    router,
    selectedVariant?.id,
    setShouldRefetchCart,
    siteSlug,
    domain,
  ])

  const product = productData?.product

  const { buttonText } = getProductPrice({
    priceInCents: product?.pricing?.priceInCents || 0,
    salePriceInCents: product?.pricing?.salePriceInCents || 0,
    maxPriceInCents: product?.pricing?.maxPriceInCents || 0,
    hasSale: product?.pricing?.hasSale,
    currency: product?.pricing?.currency || 'usd',
    isRange: product?.pricing?.isRange,
    t,
  })

  const isSoldOut = useMemo(() => {
    return selectedVariant ? selectedVariant?.isSoldOut : product?.isSoldOut
  }, [product?.isSoldOut, selectedVariant])

  const mediaAssets = useMemo(
    () => [
      ...(productData?.product?.mediaAssets || [])
        .filter((asset) => !!asset?.media)
        .map((asset) => ({ productVariantId: undefined, media: asset.media! })),
      ...(productData?.productVariants || [])
        .filter((productVariant) => productVariant?.mediaAsset)
        .map((productVariant) => ({
          productVariantId: productVariant?.id,
          media: productVariant.mediaAsset!,
        })),
    ],
    [productData?.product?.mediaAssets, productData?.productVariants],
  )

  const handleVariantSelect = useCallback(
    (variant: any) => {
      setSelectedVariant(variant)

      const index = mediaAssets.findIndex(
        (asset) => asset.productVariantId === variant.id,
      )
      setStartIndex(index)
    },
    [mediaAssets],
  )

  if (!product) {
    return <NotFound />
  }

  return (
    <div>
      {mediaAssets.length > 0 && (
        <div className="mb-4">
          <ProductMediaCarousel
            mediaAssets={mediaAssets}
            height={breakpoint === 'sm' ? 320 : DEFAULT_WIDTH}
            width={breakpoint === 'sm' ? 320 : DEFAULT_WIDTH}
            startIndex={startIndex}
          />
        </div>
      )}

      <div className="mb-4 flex flex-row justify-between gap-2">
        <div>
          <h1 className="text-lg font-bold">{product.name}</h1>
        </div>

        {!manyVariants && (
          <div className="flex flex-row items-center space-x-2 text-lg">
            {buttonText}
          </div>
        )}
      </div>

      {manyVariants && (
        <ProductVariantSelector
          variants={productData?.productVariants || []}
          selectedVariant={selectedVariant}
          onSelect={handleVariantSelect}
          product={product}
        />
      )}

      <div className="flex flex-row items-center gap-2">
        <Button
          onClick={handleAddToCart}
          className="w-full"
          loading={mutationAdd.isPending}
          disabled={isSoldOut}
        >
          {isSoldOut ? t('sold_out') : t('add_to_cart')}
        </Button>

        <Button
          variant="secondary"
          onClick={handleBuyItNow}
          className="w-full"
          loading={mutationEmptyCart.isPending || mutationAdd.isPending}
          disabled={isSoldOut}
        >
          {isSoldOut ? t('sold_out') : t('buy_it_now')}
        </Button>
      </div>

      <div
        className="prose my-4 text-current prose-headings:text-current prose-p:text-current prose-a:text-current prose-a:underline prose-strong:text-current prose-em:text-current"
        dangerouslySetInnerHTML={{
          __html: product.description || '',
        }}
      />

      {[StoreProductType.PHYSICAL, 'STORE_PRODUCT_TYPE_PHYSICAL'].includes(
        product?.type as any,
      ) && (
        <div className="mb-4">
          <div className="mb-2">
            <Label>{t('quantity')}</Label>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="secondary"
              size="xs"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
            >
              <MinusLargeIcon className="size-3" />
            </Button>

            <div className="text-base text-center">{quantity}</div>

            <Button
              variant="secondary"
              size="xs"
              onClick={() => setQuantity(quantity + 1)}
            >
              <PlusLargeIcon className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
